import React from "react";
import { ToggleGroupItem } from "@/components/ui/toggle-group";
import { cn } from "@/lib/utils";

/** Off: page-colored with a hover tint. On: filled with the primary color. */
const CHIP_STATES =
  "data-[state=off]:bg-background data-[state=off]:hover:bg-muted data-[state=on]:bg-primary data-[state=on]:text-primary-foreground";

/** A standalone rounded toggle chip; render inside a ToggleGroup. `relative`, so a badge
 * can sit on its corner. */
export const ChipItem: React.FC<{
  value: string;
  "aria-label"?: string;
  children: React.ReactNode;
}> = ({ value, "aria-label": ariaLabel, children }) => (
  <ToggleGroupItem
    value={value}
    variant="outline"
    size="sm"
    aria-label={ariaLabel}
    className={cn(
      "relative gap-1.5 rounded-full px-3 data-[state=on]:border-primary [&_svg]:size-3.5",
      CHIP_STATES
    )}
  >
    {children}
  </ToggleGroupItem>
);

/**
 * Options as one joined control (segments split by thin dividers); render inside a single-
 * or multi-select ToggleGroup.
 */
const SegmentedChips: React.FC<{ options: readonly { value: string; label: string }[] }> = ({
  options,
}) => (
  <div className="inline-flex h-8 divide-x divide-input overflow-hidden rounded-full border border-input shadow-sm">
    {options.map((option) => (
      <ToggleGroupItem
        key={option.value}
        value={option.value}
        size="sm"
        className={cn(
          "h-full whitespace-nowrap rounded-none px-3 first:pl-4 last:pr-4 focus-visible:ring-2 focus-visible:ring-inset data-[state=on]:focus-visible:ring-primary-foreground",
          CHIP_STATES
        )}
      >
        {option.label}
      </ToggleGroupItem>
    ))}
  </div>
);

export default SegmentedChips;
